import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { MiniTable } from '@/components/tables/MiniTable/MiniTable';
import { COLORS } from '@/styles/tokens/colors';
import { StateCode } from '@/types/schema';
import {
  formatCurrency,
  formatCurrencyNoDecimals,
  formatCurrencyNoDecimalsAccounting,
} from '@/utils/formatting/currency';

import { TaxDetail_StateTaxDetailFragment } from '../../graphql/TaxDetail.generated';
import {
  APPORTIONMENT_RATIO_TOOLTIP,
  getNonResidentialLongLabel,
  getNonResidentialMethod,
  getNonResidentialMethodTooltip,
  HAWAII_TAX_TOOLTIP,
  isNonResidentialStateTax,
} from '../../nonResidentialState.utils';
import { getStateTaxFields } from '../../taxes.utils';
import { getStateTaxTooltipContent } from './getStateTaxTooltip';

interface StateTaxMiniTableProps {
  stateTax: TaxDetail_StateTaxDetailFragment;
  // When set, rows whose stateCode differs render the non-residential variant.
  // Omit to render every state as residential.
  residentialStateCode?: StateCode;
}

export function StateTaxMiniTable({
  stateTax,
  residentialStateCode,
}: StateTaxMiniTableProps) {
  const {
    stateName,
    stateTaxDue,
    exemptionUsed,
    exemptionAvailable,
    valuationDiscountApplied,
    grossEstate,
    taxableEstate,
    adjustedTaxableGifts,
    ctTaxesPaid,
    amountSubjectToTax,
    inheritanceTaxCredit,
    nonTaxableTransfers,
    isTaxableAmountOverCliff,
    apportionmentRatio,
  } = useMemo(() => getStateTaxFields(stateTax), [stateTax]);

  // Currently only MD couples inheritance and estate tax, so this row only
  // surfaces when the credit is non-zero. The label is keyed off the state code
  // rather than hardcoded so it works for any future state. Rendered directly
  // under "Amount subject to tax" as a reduction against the state tax due.
  // Only wired into the standard and apportionment builders because those are
  // the layouts MD renders through (standard when residential, apportionment
  // when not); CT/IL/direct-exclusion builders never render an MD detail.
  const inheritanceTaxCreditRow = inheritanceTaxCredit.greaterThan(0)
    ? {
        label: `${stateTax.stateCode ?? 'State'} inheritance tax credit`,
        value: formatCurrencyNoDecimalsAccounting(
          inheritanceTaxCredit.abs().negated()
        ),
      }
    : null;

  const isNonResidential = isNonResidentialStateTax(
    stateTax,
    residentialStateCode
  );
  const nonResidentialMethod = isNonResidential
    ? getNonResidentialMethod(stateTax.stateCode)
    : null;

  const { showStateExemption, stateExemptionDisplayValue } = useMemo(() => {
    if (exemptionUsed.greaterThan(0)) {
      return {
        showStateExemption: true,
        stateExemptionDisplayValue: exemptionUsed, // Show exemption used
      };
    } else {
      return {
        showStateExemption: false,
        stateExemptionDisplayValue: new Decimal(0), // unused
      };
    }
  }, [exemptionUsed]);

  const stateExemptionTooltip = useMemo(() => {
    if (isTaxableAmountOverCliff) {
      // Specific copy for certain states
      if (stateTax.stateCode === StateCode.Il) {
        return `The taxable estate exceeds the IL exemption and the entire taxable estate is taxable`;
      }

      return `The taxable estate exceeds the ${
        stateTax.stateCode ?? 'state'
      } cliff and the entire taxable estate is taxable`;
    }

    return undefined;
  }, [isTaxableAmountOverCliff, stateTax.stateCode]);

  const isIllinoisTax = stateTax.stateCode === StateCode.Il;
  const isConnecticutTax = stateTax.stateCode === StateCode.Ct;

  const getConnecticutRows = () => {
    const priorCtGiftTaxPaid = ctTaxesPaid ?? new Decimal(0);
    const priorCtTaxableGifts = adjustedTaxableGifts ?? new Decimal(0);

    return compact([
      {
        label: `${stateName} estate tax`,
        labelProps: { variant: 'title-xs' as const },
        value: '',
      },
      {
        label: 'Gross estate',
        value: formatCurrencyNoDecimals(grossEstate),
      },
      {
        label: 'Charitable and marital bequests',
        value: formatCurrencyNoDecimalsAccounting(
          nonTaxableTransfers.abs().negated()
        ),
      },
      {
        label: 'Valuation discount',
        value: valuationDiscountApplied.greaterThan(0)
          ? formatCurrencyNoDecimalsAccounting(
              valuationDiscountApplied.abs().negated()
            )
          : formatCurrencyNoDecimals(new Decimal(0)),
      },
      {
        label: 'Prior CT taxable gifts',
        value: formatCurrencyNoDecimals(priorCtTaxableGifts),
      },
      {
        label: 'CT exemption deduction',
        value: exemptionAvailable.greaterThan(0)
          ? formatCurrencyNoDecimalsAccounting(
              exemptionAvailable.abs().negated()
            )
          : formatCurrencyNoDecimals(new Decimal(0)),
      },
      {
        label: 'Amount subject to tax',
        value: formatCurrencyNoDecimals(amountSubjectToTax),
        labelTooltip: stateExemptionTooltip,
      },
      {
        label: 'Initial state tax due',
        value: formatCurrencyNoDecimalsAccounting(
          stateTaxDue.plus(priorCtGiftTaxPaid).abs().negated()
        ),
        valueProps: {
          color: stateTaxDue.plus(priorCtGiftTaxPaid).greaterThan(0)
            ? COLORS.ORANGE[600]
            : undefined,
        },
      },
      {
        label: 'Prior CT gift tax paid',
        value: priorCtGiftTaxPaid.greaterThan(0)
          ? formatCurrencyNoDecimalsAccounting(
              priorCtGiftTaxPaid.abs().negated()
            )
          : formatCurrencyNoDecimals(new Decimal(0)),
      },
      {
        label: 'State tax due',
        value: formatCurrency(stateTaxDue.abs().negated(), {
          currencySign: 'accounting',
          maximumFractionDigits: 0,
        }),
        valueProps: {
          color: stateTaxDue.greaterThan(0) ? COLORS.ORANGE[600] : undefined,
        },
        labelTooltip: stateTax.stateCode
          ? getStateTaxTooltipContent(stateTax.stateCode)
          : undefined,
      },
    ]);
  };

  const getIllinoisRows = () => {
    return compact([
      {
        label: `${stateName} estate tax`,
        labelProps: { variant: 'title-xs' as const },
        value: '',
      },
      {
        label: 'Gross estate',
        value: formatCurrencyNoDecimals(grossEstate),
      },
      {
        label: 'Charitable and marital bequests',
        value: formatCurrencyNoDecimalsAccounting(
          nonTaxableTransfers.abs().negated()
        ),
      },
      {
        label: 'Valuation discount',
        value: valuationDiscountApplied.greaterThan(0)
          ? formatCurrencyNoDecimalsAccounting(
              valuationDiscountApplied.abs().negated()
            )
          : formatCurrencyNoDecimals(new Decimal(0)),
      },
      {
        label: 'Taxable estate',
        value: formatCurrencyNoDecimals(taxableEstate),
      },
      {
        label: 'Adjusted taxable gifts',
        value: formatCurrencyNoDecimals(adjustedTaxableGifts),
      },
      showStateExemption
        ? {
            label: 'Illinois exemption used',
            value: formatCurrencyNoDecimalsAccounting(
              stateExemptionDisplayValue.abs().negated()
            ),
          }
        : null,
      {
        label: 'Amount subject to tax',
        value: formatCurrencyNoDecimals(amountSubjectToTax),
        labelTooltip: stateExemptionTooltip,
      },
      {
        label: 'State tax due',
        value: formatCurrency(stateTaxDue.abs().negated(), {
          currencySign: 'accounting',
          maximumFractionDigits: 0,
        }),
        valueProps: { color: COLORS.ORANGE[600] },
        labelTooltip: stateTax.stateCode
          ? getStateTaxTooltipContent(stateTax.stateCode)
          : undefined,
      },
    ]);
  };

  const stateTaxDueTooltip = useMemo(() => {
    // HI carries its own credit-method copy in both residential and
    // non-residential cases; the generic apportionment tooltip doesn't apply.
    if (stateTax.stateCode === StateCode.Hi) {
      return HAWAII_TAX_TOOLTIP;
    }
    if (nonResidentialMethod) {
      return getNonResidentialMethodTooltip(nonResidentialMethod);
    }
    return stateTax.stateCode
      ? getStateTaxTooltipContent(stateTax.stateCode)
      : undefined;
  }, [nonResidentialMethod, stateTax]);

  const headerLabel = isNonResidential
    ? getNonResidentialLongLabel(stateTax.stateCode)
    : `${stateName} estate tax`;

  const getApportionmentRows = () => {
    return compact([
      {
        label: headerLabel,
        labelProps: { variant: 'title-xs' as const },
        value: '',
        labelTooltip: stateTaxDueTooltip,
      },
      {
        label: 'Gross estate',
        value: formatCurrencyNoDecimals(grossEstate),
      },
      {
        label: 'Charitable and marital bequests',
        value: formatCurrencyNoDecimalsAccounting(
          nonTaxableTransfers.abs().negated()
        ),
      },
      {
        label: 'Valuation discount',
        value: valuationDiscountApplied.greaterThan(0)
          ? formatCurrencyNoDecimalsAccounting(
              valuationDiscountApplied.abs().negated()
            )
          : formatCurrencyNoDecimals(new Decimal(0)),
      },
      {
        label: 'Amount subject to tax',
        value: formatCurrencyNoDecimals(amountSubjectToTax),
      },
      inheritanceTaxCreditRow,
      {
        label: 'Apportionment ratio',
        value: (apportionmentRatio ?? new Decimal(0)).dividedBy(100).toFixed(2),
        labelTooltip: APPORTIONMENT_RATIO_TOOLTIP,
      },
      {
        label: 'State tax due',
        value: formatCurrency(stateTaxDue.abs().negated(), {
          currencySign: 'accounting',
          maximumFractionDigits: 0,
        }),
        valueProps: { color: COLORS.ORANGE[600] },
      },
    ]);
  };

  const getDirectExclusionRows = () => {
    return compact([
      {
        label: headerLabel,
        labelProps: { variant: 'title-xs' as const },
        value: '',
        labelTooltip: stateTaxDueTooltip,
      },
      {
        label: 'Gross estate',
        value: formatCurrencyNoDecimals(grossEstate),
      },
      {
        label: 'Charitable and marital bequests',
        value: formatCurrencyNoDecimalsAccounting(
          nonTaxableTransfers.abs().negated()
        ),
      },
      {
        label: 'Valuation discount',
        value: valuationDiscountApplied.greaterThan(0)
          ? formatCurrencyNoDecimalsAccounting(
              valuationDiscountApplied.abs().negated()
            )
          : formatCurrencyNoDecimals(new Decimal(0)),
      },
      {
        label: 'Amount subject to tax',
        value: formatCurrencyNoDecimals(amountSubjectToTax),
      },
      {
        label: 'State tax due',
        value: formatCurrency(stateTaxDue.abs().negated(), {
          currencySign: 'accounting',
          maximumFractionDigits: 0,
        }),
        valueProps: { color: COLORS.ORANGE[600] },
      },
    ]);
  };

  // Standard rows for all other states
  const getStandardRows = () => {
    return compact([
      {
        label: headerLabel,
        labelProps: { variant: 'title-xs' as const },
        value: '',
      },
      {
        label: 'Gross estate',
        value: formatCurrencyNoDecimals(grossEstate),
      },
      {
        label: 'Charitable and marital bequests',
        value: formatCurrencyNoDecimalsAccounting(
          nonTaxableTransfers.abs().negated()
        ),
      },
      {
        label: 'Valuation discount',
        value: valuationDiscountApplied.greaterThan(0)
          ? formatCurrencyNoDecimalsAccounting(
              valuationDiscountApplied.abs().negated()
            )
          : formatCurrencyNoDecimals(new Decimal(0)),
      },
      showStateExemption
        ? {
            label: `${stateName} exemption used`,
            value: formatCurrencyNoDecimalsAccounting(
              stateExemptionDisplayValue.abs().negated()
            ),
          }
        : null,
      {
        label: 'Amount subject to tax',
        value: formatCurrencyNoDecimals(amountSubjectToTax),
        labelTooltip: stateExemptionTooltip,
      },
      inheritanceTaxCreditRow,
      {
        label: 'State tax due',
        value: formatCurrency(stateTaxDue.abs().negated(), {
          currencySign: 'accounting',
          maximumFractionDigits: 0,
        }),
        valueProps: { color: COLORS.ORANGE[600] },
        labelTooltip: stateTaxDueTooltip,
      },
    ]);
  };

  const getRows = () => {
    if (nonResidentialMethod === 'apportionment') {
      return getApportionmentRows();
    }
    if (nonResidentialMethod === 'directExclusion') {
      return getDirectExclusionRows();
    }
    if (isConnecticutTax) {
      return getConnecticutRows();
    }
    if (isIllinoisTax) {
      return getIllinoisRows();
    }
    return getStandardRows();
  };

  return <MiniTable variant="tally" rows={getRows()} />;
}
